import * as React from "react"
import { DashboardLayout } from "@/components/dashboard/dashboard-layout"
import { Button } from "@/components/ui/button"
import { Check, AlertCircle, Calendar, Loader2, XCircle, Clock, AlertTriangle, Lock } from "lucide-react"
import { useAvailablePackages } from "@/lib/api/package-purchase"
import { usePackageStatus } from "@/hooks/usePackageStatus"
import { useNavigate } from "react-router-dom"
import { Card } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Progress } from "@/components/ui/progress"
import { useAuth } from "@/src/contexts/AuthContext"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
// import { AutoRenewalSettings } from "./components/AutoRenewalSettings"

export default function PackagesSettingsPage() {
  const navigate = useNavigate()
  const { hasPermission } = useAuth()
  const canPurchase = hasPermission('credit:purchase')
  const { data: packagesData, isLoading: isLoadingPackages } = useAvailablePackages()
  const { packageStatus, loading: packageLoading, cancelPackage } = usePackageStatus()
  const [isCanceling, setIsCanceling] = React.useState(false)

  // Debug: Store in window for browser console access
  if (typeof window !== 'undefined') {
    (window as any).__debugPackagesData = packagesData;
    (window as any).__debugCurrentPackage = packagesData?.currentPackage;
  }
  console.log('📦 Packages data in component:', packagesData)
  console.log('📦 Current package specifically:', packagesData?.currentPackage)
  console.log('📦 Full JSON:', JSON.stringify(packagesData?.currentPackage, null, 2))

  const handleSelectPackage = (packageId: string) => {
    navigate(`/settings/packages/purchase/${packageId}`)
  }

  const handleCancelPackage = async () => {
    try {
      setIsCanceling(true)
      await cancelPackage()
      // Success message is shown by the hook
    } catch (error) {
      console.error('Failed to cancel package:', error)
    } finally {
      setIsCanceling(false)
    }
  }

  const isCurrentPackage = (packageName: string) => {
    return packagesData?.currentPackage?.name === packageName.toLowerCase()
  }

  const hasPendingPackage = !!packagesData?.pendingPackage

  const isLoading = isLoadingPackages

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Page Title */}
        <h1 className="text-2xl font-bold">ตั้งค่าแพ็กเกจ</h1>

        {/* Current Package Information */}
        {isLoading || packageLoading ? (
          <Card className="p-6 max-w-lg">
            <div className="flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          </Card>
        ) : packageStatus && packageStatus.status !== 'expired' ? (
          <div className="space-y-4">
            {/* Current Package Card with Enhanced Status */}
            <Card className={`p-4 max-w-lg ${
              packageStatus.status === 'cancelled_active' ? 'bg-yellow-50 border-yellow-200' :
              'bg-white'
            }`}>
              <div className="flex justify-between items-start mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold">แพ็กเกจปัจจุบัน:</span>
                    <span className="text-primary">{packageStatus.name}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    {packageStatus.status === 'active' && !packageStatus.cancelled_at && (
                      <Badge className="bg-green-100 text-green-700 border-green-300">
                        <Check className="w-3 h-3 mr-1" />
                        กำลังใช้งาน
                      </Badge>
                    )}
                    {packageStatus.status === 'cancelled_active' && (
                      <Badge className="bg-yellow-100 text-yellow-700 border-yellow-300">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        ยกเลิกแล้ว (ใช้งานได้ถึงวันหมดอายุ)
                      </Badge>
                    )}
                    {packageStatus.auto_renewal && (
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
                        <Clock className="w-3 h-3 mr-1" />
                        ต่ออายุอัตโนมัติ
                      </Badge>
                    )}
                  </div>
                </div>
                {packageStatus.days_remaining !== null && packageStatus.days_remaining >= 0 && (
                  <div className="text-right">
                    <div className="text-xs text-gray-600">คงเหลือ</div>
                    <div className={`text-2xl font-bold ${
                      packageStatus.is_expiring_soon ? 'text-orange-600' : 'text-primary'
                    }`}>
                      {packageStatus.days_remaining}
                    </div>
                    <div className="text-xs text-gray-600">วัน</div>
                  </div>
                )}
              </div>

              {/* Progress Bar for Days Remaining */}
              {packageStatus.days_remaining !== null && packageStatus.expires_at && (
                <div className="mb-4">
                  <div className="flex justify-between text-xs text-gray-600 mb-1">
                    <span>วันเริ่มต้น</span>
                    <span>วันหมดอายุ: {new Date(packageStatus.expires_at).toLocaleDateString('th-TH', { 
                      day: 'numeric', 
                      month: 'short', 
                      year: 'numeric' 
                    })}</span>
                  </div>
                  <Progress 
                    value={Math.max(0, (packageStatus.days_remaining / 30) * 100)} 
                    className={`h-2 ${
                      packageStatus.is_expiring_soon ? 'bg-orange-200' : 'bg-gray-200'
                    }`}
                  />
                </div>
              )}

              {/* Cancellation Warning */}
              {packageStatus.status === 'cancelled_active' && packageStatus.cancelled_at && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-semibold text-yellow-900">Package ถูกยกเลิกแล้ว</p>
                      <p className="text-yellow-800 mt-1">
                        ยกเลิกเมื่อ: {new Date(packageStatus.cancelled_at).toLocaleDateString('th-TH', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                      <p className="text-yellow-800 mt-1">
                        คุณสามารถใช้งานได้จนถึง: {new Date(packageStatus.expires_at!).toLocaleDateString('th-TH', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Expiring Soon Warning */}
              {packageStatus.is_expiring_soon && packageStatus.status === 'active' && !packageStatus.cancelled_at && (
                <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Clock className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-semibold text-orange-900">Package ใกล้หมดอายุ</p>
                      <p className="text-orange-800 mt-1">
                        เหลือเวลาอีก {packageStatus.days_remaining} วัน กรุณาต่ออายุหรือเปิดการต่ออายุอัตโนมัติ
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <hr className="my-3 border-gray-200" />

              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span>ราคา</span>
                  <span>{packageStatus.price.toLocaleString()} บาท/เดือน</span>
                </div>
                <div className="flex justify-between">
                  <span>เครดิตในแพ็กเกจ</span>
                  <span className="text-primary">
                    {packageStatus.included_credits === -1 
                      ? 'Unlimited' 
                      : packageStatus.included_credits.toLocaleString()} เครดิต
                  </span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="pt-3 space-y-3">
                {/* Cancel Package Button - Only show if not already cancelled */}
                {!packageStatus.cancelled_at && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="w-full border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                        disabled={isCanceling}
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        ยกเลิกแพ็กเกจปัจจุบัน
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                          <AlertCircle className="w-5 h-5 text-red-600" />
                          ยืนยันการยกเลิกแพ็กเกจ
                        </AlertDialogTitle>
                        <AlertDialogDescription className="space-y-2 pt-2">
                          <p className="font-semibold text-gray-900">
                            คุณต้องการยกเลิกแพ็กเกจ "{packageStatus.name}" ใช่หรือไม่?
                          </p>
                          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 space-y-1 text-sm">
                            <p className="font-medium text-amber-900">⚠️ สิ่งที่จะเกิดขึ้น:</p>
                            <ul className="list-disc list-inside text-amber-800 space-y-1">
                              <li>Package จะถูกยกเลิกทันที</li>
                              <li>คุณยังสามารถใช้งานได้จนถึงวันหมดอายุ ({packageStatus.expires_at ? new Date(packageStatus.expires_at).toLocaleDateString('th-TH') : '-'})</li>
                              <li>การต่ออายุอัตโนมัติจะถูกปิด</li>
                              <li>เครดิตคงเหลือจะหมดอายุตามวันหมดอายุของ package</li>
                            </ul>
                          </div>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>ยกเลิก</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={handleCancelPackage}
                          className="bg-red-600 hover:bg-red-700 text-white focus:ring-red-500"
                          disabled={isCanceling}
                        >
                          {isCanceling ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              กำลังยกเลิก...
                            </>
                          ) : (
                            'ยืนยันการยกเลิก'
                          )}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </Card>

            {/* Pending Package Card (if exists) */}
            {hasPendingPackage && packagesData?.pendingPackage && (
              <Card className="p-4 max-w-lg bg-blue-50 border-blue-200">
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold text-blue-900">
                      📋 Package ถัดไป: {packagesData.pendingPackage.name}
                    </p>
                    <p className="text-sm text-blue-800 mt-1">
                      จะเริ่มใช้งานเมื่อแพ็กเกจปัจจุบันหมดอายุ
                    </p>
                    {packagesData.pendingPackage.purchased_at && (
                      <p className="text-xs text-blue-700 mt-1">
                        ซื้อเมื่อ: {new Date(packagesData.pendingPackage.purchased_at).toLocaleDateString('th-TH')}
                      </p>
                    )}
                  </div>
                </div>
              </Card>
            )}
          </div>
        ) : null}

        {/* Auto-Renewal Settings - Commented out temporarily, using new API */}
        {/* {!isLoading && packagesData?.currentPackage && packagesData.currentPackage.name !== 'starter' && (
          <AutoRenewalSettings />
        )} */}

        {/* Call to Action Section */}
        <div className="text-center py-4">
          <h2 className="text-3xl font-bold mb-2">
            เลือก<span className="text-primary">แพ็กเกจ</span>
            ที่เหมาะสมกับคุณ
          </h2>
          <p className="text-gray-600">
            คุณสามารถใช้ <span className="text-red-500 font-semibold">SlipPro</span> ได้ทุกแพ็กเกจ
          </p>
        </div>

        {/* Pending Package Notice */}
        {hasPendingPackage && (
          <Card className="p-4 max-w-3xl mx-auto bg-amber-50 border-amber-200">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-900">
                  คุณมี Package รออยู่แล้ว
                </p>
                <p className="text-sm text-amber-800 mt-1">
                  กรุณารอจนกว่าแพ็กเกจจะเริ่มใช้งานก่อนซื้อใหม่
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Package List */}
        {isLoadingPackages ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : packagesData?.availablePackages && packagesData.availablePackages.length > 0 ? (
          <div className="space-y-8">
            {/* Group packages by tier if needed, or show all */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {packagesData.availablePackages
                .filter((pkg: any) => pkg.name !== 'owner')
                .sort((a: any, b: any) => parseFloat(a.price_monthly || '0') - parseFloat(b.price_monthly || '0'))
                .map((pkg: any) => {
                const isCurrent = isCurrentPackage(pkg.name)
                const isDisabled = hasPendingPackage || isCurrent

                return (
                  <Card 
                    key={pkg.id} 
                    className={`p-6 space-y-4 relative transition-all ${
                      isCurrent 
                        ? 'border-2 border-green-500 bg-green-50 shadow-lg ring-2 ring-green-200' 
                        : 'hover:shadow-lg'
                    }`}
                  >
                    {/* Current Badge */}
                    {isCurrent && (
                      <div className="absolute top-2 right-2">
                        <span className="px-3 py-1 bg-green-600 text-white text-xs font-semibold rounded-full shadow-sm">
                          ✓ ใช้งานอยู่
                        </span>
                      </div>
                    )}

                    {/* Package Header */}
                    <div>
                      <h4 className="text-lg font-semibold mb-2">{pkg.display_name || pkg.name}</h4>
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-bold">{parseFloat(pkg.price_monthly || '0').toLocaleString()}</span>
                        <span className="text-gray-600">บาท</span>
                      </div>
                    </div>

                    <div className="border-t pt-4">
                      <p className="text-sm text-gray-600">
                        {pkg.included_credits === -1 
                          ? 'Unlimited' 
                          : pkg.included_credits?.toLocaleString() || 0} เครดิต/เดือน
                      </p>
                    </div>

                    {/* Select Button */}
                    <Button 
                      variant={isCurrent ? "outline" : "default"}
                      className="w-full"
                      onClick={() => handleSelectPackage(pkg.id)}
                      disabled={isDisabled || !canPurchase}
                      title={
                        !canPurchase
                          ? "Owner package required to purchase"
                          : hasPendingPackage 
                          ? "คุณมี package รออยู่แล้ว" 
                          : isCurrent 
                          ? "คุณใช้แพ็กเกจนี้อยู่แล้ว" 
                          : ""
                      }
                    >
                      {!canPurchase && <Lock className="h-4 w-4 mr-2" />}
                      {isCurrent ? 'แพ็กเกจปัจจุบัน' : hasPendingPackage ? 'มี Package รออยู่' : 'เลือกแพ็กเกจนี้'}
                    </Button>
                    {!canPurchase && !isCurrent && !hasPendingPackage && (
                      <p className="text-xs text-orange-600 flex items-center justify-center gap-1 mt-2">
                        <Lock className="h-3 w-3" />
                        <span>ซื้อ package ต้องมีสิทธิ์ <strong>Owner</strong></span>
                      </p>
                    )}

                    {/* Features */}
                    {pkg.features && pkg.features.length > 0 && (
                      <div className="space-y-3 pt-4 border-t">
                        <h5 className="font-semibold">Features</h5>
                        <div className="space-y-2">
                          {pkg.features.map((feature: string, idx: number) => (
                            <div key={idx} className="flex items-start gap-2">
                              <Check className="w-4 h-4 text-gray-600 mt-0.5 flex-shrink-0" />
                              <span className="text-sm text-gray-700">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </Card>
                )
              })}
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-600">ไม่พบข้อมูลแพ็กเกจ</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}
